import { lexer } from 'marked';

/**
 * Parsing for the restricted CommonMark subset used by the disclosure text.
 *
 * Framework-free on purpose. This file holds the two pieces with real failure modes — the link
 * scheme allow-list and the token normalisation — and imports nothing from Angular, so it can be
 * executed and checked directly. The `Web` app has no unit-test runner, so a module that needs
 * TestBed to exercise is a module that never gets exercised at all.
 *
 * The renderer that consumes this produces no HTML string: the model below is rendered through
 * ordinary Angular bindings, which escape by construction. Constructs outside the subset are
 * dropped here, by omission, rather than stripped afterwards by a sanitiser — the same shape as
 * the backend walking the flexmark AST into its own PDF text-flow helper.
 *
 * Supported: ATX headings, paragraphs, bold, italic, ordered and unordered lists, links.
 * Not supported, by design: raw HTML, tables, images, code blocks, block quotes.
 */

/** A run of text with its accumulated emphasis, and a link target if it is one. */
export interface MdInline {
  text: string;
  bold?: boolean;
  italic?: boolean;
  /** Present and non-null only for a link whose scheme passed `isSafeHref`. */
  href?: string | null;
}

export interface MdListItem {
  inlines: MdInline[];
  /** Nesting depth; 0 for a top-level item. */
  indent: number;
  /**
   * The rendered marker — "1." / "2." for an ordered list, "•" for a bulleted one.
   *
   * Computed while parsing rather than from the item's index in the flat array: nested lists are
   * flattened into that same array, so a positional marker would number a sub-list's first item
   * after its parent instead of restarting.
   */
  marker: string;
}

export interface MdBlock {
  kind: 'heading' | 'paragraph' | 'list';
  /** 1-6, for a heading. */
  level?: number;
  ordered?: boolean;
  start?: number;
  /** For heading and paragraph. */
  inlines?: MdInline[];
  /** For list. */
  items?: MdListItem[];
}

/**
 * Schemes a link may use.
 *
 * Required even though no HTML is ever produced: `javascript:` is a perfectly valid CommonMark
 * link target, and an `href` binding would still navigate. A link whose target fails this renders
 * as plain text — the words are kept, because dropping content from a legal disclosure is worse
 * than dropping a link.
 */
export const SAFE_SCHEMES = ['http:', 'https:', 'mailto:'];

export function isSafeHref(href: string | null | undefined): boolean {
  if (!href) return false;

  const trimmed = href.trim();
  if (!trimmed) return false;

  // Relative targets carry no scheme and cannot leave the origin.
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return true;

  try {
    const parsed = new URL(trimmed, 'https://placeholder.invalid');
    return SAFE_SCHEMES.indexOf(parsed.protocol) !== -1;
  } catch {
    return false;
  }
}

/** Parse Markdown into the subset model. Never throws: a broken body degrades to plain text. */
export function parseMarkdownSubset(source: string | null | undefined): MdBlock[] {
  const text = source || '';
  if (!text.trim()) return [];

  let tokens: any[];
  try {
    tokens = lexer(text) as any[];
  } catch (e) {
    // A disclosure that will not parse must not blank the modal: the signer still has to see
    // what they are being asked to consent to.
    return [{ kind: 'paragraph', inlines: [{ text }] }];
  }

  const blocks: MdBlock[] = [];
  for (const token of tokens) {
    appendBlock(token, blocks);
  }
  return blocks;
}

function appendBlock(token: any, blocks: MdBlock[]): void {
  switch (token?.type) {
    case 'heading':
      blocks.push({
        kind: 'heading',
        level: Math.min(Math.max(token.depth || 1, 1), 6),
        inlines: flatten(token.tokens)
      });
      break;

    case 'paragraph':
    case 'text':
      blocks.push({ kind: 'paragraph', inlines: flatten(token.tokens) });
      break;

    case 'list': {
      const items: MdListItem[] = [];
      appendList(token, items, 0);
      blocks.push({
        kind: 'list',
        ordered: !!token.ordered,
        start: typeof token.start === 'number' && token.start > 0 ? token.start : 1,
        items
      });
      break;
    }

    // html, table, image, code, blockquote, hr, space — outside the subset, not rendered.
    default:
      break;
  }
}

/**
 * Append one list's items, restarting the ordinal at this level.
 *
 * Nested lists are flattened into the same array at a deeper indent rather than dropped: the
 * subset does not promise nesting, but silently losing a sub-clause of a legal text would be
 * worse than rendering it plainly.
 */
function appendList(list: any, out: MdListItem[], indent: number): void {
  const ordered = !!list.ordered;
  let ordinal = typeof list.start === 'number' && list.start > 0 ? list.start : 1;

  for (const item of list.items || []) {
    const inlines: MdInline[] = [];
    const nested: any[] = [];

    for (const child of item?.tokens || []) {
      if (child.type === 'list') {
        nested.push(child);
      } else if (child.type === 'text' || child.type === 'paragraph') {
        inlines.push(...flatten(child.tokens));
      }
    }

    out.push({ inlines, indent, marker: ordered ? `${ordinal}.` : '•' });
    ordinal++;

    for (const child of nested) {
      appendList(child, out, indent + 1);
    }
  }
}

/** Collapse an inline token tree into flat runs carrying their accumulated emphasis. */
function flatten(
  tokens: any[] | undefined,
  bold = false,
  italic = false,
  href: string | null = null
): MdInline[] {
  const out: MdInline[] = [];

  for (const token of tokens || []) {
    switch (token?.type) {
      case 'strong':
        out.push(...flatten(token.tokens, true, italic, href));
        break;
      case 'em':
        out.push(...flatten(token.tokens, bold, true, href));
        break;
      case 'link': {
        const safe = isSafeHref(token.href) ? token.href : null;
        const inner = flatten(token.tokens, bold, italic, safe);
        out.push(...(inner.length ? inner : [{ text: token.text || '', bold, italic, href: safe }]));
        break;
      }
      case 'br':
        out.push({ text: '\n', bold, italic, href });
        break;
      case 'escape':
      case 'codespan':
      case 'text':
        if (token.tokens?.length) {
          out.push(...flatten(token.tokens, bold, italic, href));
        } else if (token.text) {
          out.push({ text: token.text, bold, italic, href });
        }
        break;
      // image, html and anything else contribute nothing.
      default:
        break;
    }
  }

  return out;
}
